// src/components/ProductCard.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CategoryNavigator from "./CategoryNavigator";
import { HeartFill, Heart } from "react-bootstrap-icons";
import { Bag } from "react-bootstrap-icons";
import { toast } from "react-toastify";
import axios from "axios";

const ProductCard = ({ item }) => {
  const navigate = useNavigate();
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [loadingWishlist, setLoadingWishlist] = useState(false);

  const customer = JSON.parse(sessionStorage.getItem("active-user")) || null;
  const userId = customer?.id ?? null;

  // Keys
  const WISHLIST_KEY = "wishlist";
  const CART_KEY = "cart";

  // Local storage helpers (guest fallback)
  const readWishlistLocal = () => {
    try {
      const raw = localStorage.getItem(WISHLIST_KEY);
      if (!raw) return [];
      return JSON.parse(raw);
    } catch (e) {
      console.error("readWishlist error", e);
      return [];
    }
  };

  const writeWishlistLocal = (arr) => {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(arr));
      window.dispatchEvent(new Event("storage")); // for components listening to storage
      window.dispatchEvent(
        new CustomEvent("wishlist-updated", { detail: { count: arr.length } })
      );
    } catch (e) {
      console.error("writeWishlist error", e);
    }
  };

  // Cart local helpers (unchanged)
  const readCart = () => {
    try {
      const raw = localStorage.getItem(CART_KEY);
      if (!raw) return [];
      return JSON.parse(raw);
    } catch (e) {
      console.error("readCart error", e);
      return [];
    }
  };

  const writeCart = (arr) => {
    try {
      localStorage.setItem(CART_KEY, JSON.stringify(arr));
      window.dispatchEvent(new Event("storage"));
    } catch (e) {
      console.error("writeCart error", e);
    }
  };

  // If logged in: fetch wishlist from backend once (to mark heart)
  useEffect(() => {
    let mounted = true;

    const init = async () => {
      // if logged in -> fetch wishlist from backend
      if (userId) {
        setLoadingWishlist(true);
        try {
          const resp = await axios.get(
            `https://goldenhides.in/api/wishlist/fetch?userId=${userId}`
          );
          const data = resp.data || {};
          const list = Array.isArray(data.wishlist) ? data.wishlist : [];
          // wishlist items structure may be {id, userId, product: {...}, ...}
          const exists = list.some((entry) => {
            const pid = entry.product?.id ?? entry.productId ?? entry.productId;
            return String(pid) === String(item.id);
          });
          if (mounted) setIsWishlisted(Boolean(exists));
        } catch (err) {
          console.warn("Failed to fetch wishlist from server", err);
          // fallback: also check localStorage if present (optional)
          const local = readWishlistLocal();
          const existsLocal = local.some(
            (it) => String(it.productId) === String(item.id)
          );
          if (mounted) setIsWishlisted(Boolean(existsLocal));
        } finally {
          if (mounted) setLoadingWishlist(false);
        }
      } else {
        // not logged in: check localStorage
        const local = readWishlistLocal();
        const existsLocal = local.some(
          (it) => String(it.productId) === String(item.id)
        );
        if (mounted) setIsWishlisted(Boolean(existsLocal));
      }
    };

    init();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id, userId]); // re-run when item or user changes

  const getShortDescription = (text, wordLimit) => {
    if (!text) return "";
    const words = text.trim().split(/\s+/);
    if (words.length <= wordLimit) return text;
    return words.slice(0, wordLimit).join(" ") + "...";
  };

  const handleNavigateToProduct = (e) => {
    e?.preventDefault?.();
    const productId = item.id;
    const categoryId = item?.category?.id ?? "";
    navigate(`/product/${productId}/category/${categoryId}`);
  };

  // Server API calls
  const addWishlistOnServer = async (userId, productId) => {
    // POST /api/wishlist/product/add  { userId, productId }
    const url = `https://goldenhides.in/api/wishlist/product/add`;
    try {
      const res = await axios.post(url, { userId, productId });
      return res.data;
    } catch (err) {
      console.error("addWishlistOnServer error", err);
      throw err;
    }
  };

  const removeWishlistOnServer = async (userId, productId) => {
    // NOTE: your backend remove endpoint/method may differ.
    // I used POST /api/wishlist/product/remove with same body for symmetry.
    // If your backend uses DELETE or GET, change this accordingly.
    const url = `https://goldenhides.in/api/wishlist/product/remove`;
    try {
      const res = await axios.post(url, { userId, productId });
      return res.data;
    } catch (err) {
      console.error("removeWishlistOnServer error", err);
      throw err;
    }
  };

  const toggleWishlist = async (e) => {
    e.stopPropagation();
    e.preventDefault();

    // if logged in -> call server
    if (userId) {
      if (loadingWishlist) return;
      setLoadingWishlist(true);
      try {
        if (isWishlisted) {
          // remove from server
          const body = await removeWishlistOnServer(userId, item.id);
          if (body?.success) {
            setIsWishlisted(false);
            toast.info("Removed from wishlist");
            // notify others
            window.dispatchEvent(
              new CustomEvent("wishlist-updated", {
                detail: { removedProductId: item.id },
              })
            );
          } else {
            toast.error(body?.responseMessage || "Remove failed");
          }
        } else {
          // add to server
          const body = await addWishlistOnServer(userId, item.id);
          if (body?.success) {
            setIsWishlisted(true);
            toast.success("Added to wishlist");
            window.dispatchEvent(
              new CustomEvent("wishlist-updated", {
                detail: { addedProductId: item.id },
              })
            );
          } else {
            toast.error(body?.responseMessage || "Add to wishlist failed");
          }
        }
      } catch (err) {
        toast.error("Server error", {
          position: "top-center",
          autoClose: 1200,
        });
      } finally {
        setLoadingWishlist(false);
      }
      return;
    }

    // guest flow => localStorage
    const list = readWishlistLocal();
    const idx = list.findIndex(
      (it) => String(it.productId) === String(item.id)
    );
    if (idx > -1) {
      list.splice(idx, 1);
      writeWishlistLocal(list);
      setIsWishlisted(false);
      toast.info("Removed from wishlist");
      return;
    }

    const entry = {
      productId: String(item.id),
      productName: item.title || "",
      productImage: item.imageName || "",
      categoryId: item.category?.id ?? "",
      categoryName: item.category?.title ?? "",
      categorySizeId: null,
      size: "",
      price: item.price != null ? Number(item.price) : undefined,
      description: item.description || "",
      createdAt: String(Date.now()),
    };

    list.push(entry);
    writeWishlistLocal(list);
    setIsWishlisted(true);
    toast.success("Added to wishlist");
  };

  const addToLocalCart = (e) => {
    e.stopPropagation();
    e.preventDefault();

    const qty = 1;
    const cart = readCart();
    const productId = String(item.id);

    const idx = cart.findIndex(
      (it) =>
        String(it.productId) === productId &&
        (it.categorySizeId == null || it.categorySizeId === "")
    );
    if (idx > -1) {
      cart[idx].quantity = Number(cart[idx].quantity || 0) + qty;
      cart[idx].totalPrice =
        Number(cart[idx].quantity) * Number(cart[idx].price || item.price || 0);
    } else {
      cart.push({
        quantity: qty,
        userId: null,
        productId,
        categoryId: item.category?.id || "",
        categoryName: item.category?.title || "",
        categorySizeId: null,
        size: "",
        price: Number(item.price) || 0,
        totalPrice: qty * (Number(item.price) || 0),
        productName: item.title || "",
        productImage: item.imageName || "",
      });
    }

    writeCart(cart);
    toast.success("Added to cart");
  };

  return (
    <div className="col">
      <div
        className="pantaloons-card h-100 position-relative"
        onClick={handleNavigateToProduct}
        style={{ cursor: "pointer" }}
      >
        {/* Badge (Top Left) */}
        <div className="pantaloons-badge">
          <CategoryNavigator
            item={{ id: item.category.id, title: item.category.title }}
          />
        </div>

        {/* Wishlist + Cart Icons (Top Right) */}
        <div className="pantaloons-icons">
          <button
            className={`pantaloons-icon-btn ${
              isWishlisted ? "heart-active" : ""
            }`}
            aria-label="wishlist"
            onClick={toggleWishlist}
            disabled={loadingWishlist}
          >
            {isWishlisted ? <HeartFill color="#e83e8c" /> : <Heart />}
          </button>

          <button
            className="pantaloons-icon-btn"
            aria-label="add to bag"
            onClick={(e) => {
              // If user is logged in you'd probably call add-to-cart API,
              // but here we keep the same behavior: navigate to product page
              // (product page handles add-to-cart with size/quantity).
              e.stopPropagation();
              handleNavigateToProduct(e);
            }}
          >
            <Bag />
          </button>
        </div>

        {/* Product Image */}
        <div className="pantaloons-image-wrap">
          <img
            src={`https://goldenhides.in/api/product/${item.imageName}`}
            alt={item.title}
            className="pantaloons-image"
          />
        </div>

        {/* Product Details */}
        <div className="pantaloons-body text-center">
          <h6 className="pantaloons-brand text-uppercase mb-1">
            {item.category.title}
          </h6>
          <h5 className="pantaloons-title mb-1">{item.title}</h5>
          <p className="pantaloons-sub text-muted mb-2">
            {getShortDescription(item.description, 5)}
          </p>
          <h5 className="pantaloons-price mb-0">₹{item.price}</h5>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
